import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterBusinessRequest,
  PaginatedResponse
} from '@saas-platform/shared-types';

const LOCATION_STORAGE_KEY = 'pos:selectedLocationId';
const USER_STORAGE_KEY = 'user';

class ApiService {
  private _client: AxiosInstance;
  private baseURL: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<void> | null = null;

  // Public getter for client (for Phase 2 API files)
  get client(): AxiosInstance {
    return this._client;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  private setAuthState(user?: any, _token?: string) {
    localStorage.removeItem('accessToken');
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      if (user.locationId) {
        localStorage.setItem(LOCATION_STORAGE_KEY, user.locationId);
      }
    }
  }

  private clearAuthState() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(LOCATION_STORAGE_KEY);
  }

  private shouldAttachLocationHeader(url?: string): boolean {
    if (!url) return true;

    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    return !(
      path.startsWith('/api/auth/') ||
      path === '/api/locations' ||
      path.startsWith('/api/locations/')
    );
  }

  constructor() {
    this.baseURL = (import.meta as any).env?.VITE_API_BASE_URL;

    this._client = axios.create({
      baseURL: this.baseURL,
      timeout: 60000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor: attach bearer token from localStorage and scoped dashboard context
    this._client.interceptors.request.use(
      (config) => {
        // Browser automatically attaches httpOnly session cookie (withCredentials: true)
        const token = localStorage.getItem('accessToken');
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        const locationId = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (locationId && locationId !== 'all' && this.shouldAttachLocationHeader(config.url)) {
          if (config.headers['X-Location-Id'] === undefined) {
            config.headers['X-Location-Id'] = locationId;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to recover the session from httpOnly cookies.
    this._client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry auth endpoints - these should handle their own errors
        const isAuthEndpoint = originalRequest.url?.includes('/api/auth/login') ||
          originalRequest.url?.includes('/api/auth/2fa/verify-login') ||
          originalRequest.url?.includes('/api/auth/register') ||
          originalRequest.url?.includes('/api/auth/refresh') ||
          originalRequest.url?.includes('/api/auth/session') ||
          originalRequest.url?.includes('/api/auth/logout');

        // If auth endpoint fails, don't try to refresh - just reject the error
        if (error.response?.status === 401 && isAuthEndpoint) {
          // If refresh endpoint fails, clear local auth state and redirect
          if (originalRequest.url?.includes('/api/auth/refresh')) {
            this.isRefreshing = false;
            this.refreshPromise = null;
            this.clearAuthState();
            // Only redirect if we're not already on the login page
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          // For login/register, just reject - don't redirect (let the component handle it)
          return Promise.reject(error);
        }

        // Handle 401 errors for other endpoints (not auth endpoints)
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          // If already refreshing, wait for that to complete
          if (this.isRefreshing && this.refreshPromise) {
            try {
              await this.refreshPromise;
              return this._client(originalRequest);
            } catch (refreshError) {
              // Refresh failed, redirect to login
              this.isRefreshing = false;
              this.refreshPromise = null;
              this.clearAuthState();
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
              return Promise.reject(refreshError);
            }
          } else {
            // Start refresh process
            this.isRefreshing = true;
            this.refreshPromise = this.refreshToken().catch((refreshError) => {
              // Refresh failed, redirect to login
              this.isRefreshing = false;
              this.refreshPromise = null;
              this.clearAuthState();
              if (window.location.pathname !== '/login') {
                window.location.href = '/login';
              }
              throw refreshError;
            });

            try {
              await this.refreshPromise;
              this.isRefreshing = false;
              this.refreshPromise = null;

              return this._client(originalRequest);
            } catch (refreshError) {
              return Promise.reject(refreshError);
            }
          }
        }
        // Handle 402 Payment Required errors (Subscription inactive / restricted writes)
        if (error.response?.status === 402) {
          window.dispatchEvent(
            new CustomEvent('billing:subscription-inactive', { detail: error.response?.data }),
          );
        }

        // Handle 403 Plan restriction errors
        if (
          error.response?.status === 403 &&
          (error.response?.data?.errorCode === 'PLAN_UPGRADE_REQUIRED' ||
            error.response?.data?.errorCode === 'PLAN_LIMIT_REACHED')
        ) {
          window.dispatchEvent(
            new CustomEvent('billing:plan-restricted', { detail: error.response?.data }),
          );
        }

        // If the backend returned a standardized error message, use that instead of the generic Axios message
        const isHtml = (val: unknown) =>
          typeof val === 'string' &&
          (val.trim().startsWith('<!DOCTYPE') || val.trim().startsWith('<html') || /<[a-z][\s\S]*>/i.test(val));

        if (error.response?.data?.error && !isHtml(error.response.data.error)) {
          error.message = error.response.data.error;
        } else if (error.response?.data?.message && !isHtml(error.response.data.message)) {
          error.message = error.response.data.message;
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this._client.post(
      '/api/auth/login',
      credentials
    );

    // Check for standardized error response
    if (!response.data.success) {
      const error = response.data as any;
      const errorMessage = error.error || error.message || 'Login failed';
      const errorCode = error.errorCode || 'UNKNOWN_ERROR';
      const validationErrors = error.validationErrors;

      const errorObj: any = new Error(errorMessage);
      errorObj.errorCode = errorCode;
      if (validationErrors) {
        errorObj.validationErrors = validationErrors;
      }
      throw errorObj;
    }

    if (!response.data.data) {
      throw new Error('Unable to sign in. Please try again.');
    }

    if (response.data.data.twoFactorRequired) {
      return response.data.data;
    }

    const { user, accessToken } = response.data.data;

    if (!user) {
      throw new Error('Unable to sign in. Please try again.');
    }

    this.setAuthState(user, accessToken);

    return response.data.data;
  }

  async verifyTwoFactorLogin(payload: {
    code: string;
    isBackupCode?: boolean;
    trustDevice?: boolean;
  }): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this._client.post(
      '/api/auth/2fa/verify-login',
      payload
    );

    if (!response.data.success || !response.data.data?.user) {
      const error = response.data as any;
      throw new Error(error.error || error.message || 'Invalid two-factor verification code');
    }

    const { user, accessToken } = response.data.data;
    this.setAuthState(user, accessToken);
    return response.data.data;
  }

  async sendRegisterVerificationCode(
    email: string,
    address: { postcode: string; country: string }
  ): Promise<{ message: string }> {
    const response: AxiosResponse<ApiResponse<{ message: string }>> = await this._client.post(
      '/api/auth/register/send-verification-code',
      {
        email: email.trim().toLowerCase(),
        postcode: address.postcode.trim(),
        country: address.country.trim(),
      }
    );
    if (!response.data.success || !response.data.data) {
      const err = response.data as any;
      const msg = err.error || err.message || 'Failed to send verification code';
      const e: any = new Error(msg);
      if (err.validationErrors) e.validationErrors = err.validationErrors;
      throw e;
    }
    return response.data.data;
  }

  async register(userData: RegisterBusinessRequest): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this._client.post(
      '/api/auth/register',
      userData
    );

    // Check for standardized error response
    if (!response.data.success) {
      const error = response.data as any;
      const errorMessage = error.error || error.message || 'Registration failed';
      const errorCode = error.errorCode || 'UNKNOWN_ERROR';
      const validationErrors = error.validationErrors;

      const errorObj: any = new Error(errorMessage);
      errorObj.errorCode = errorCode;
      if (validationErrors) {
        errorObj.validationErrors = validationErrors;
      }
      throw errorObj;
    }

    if (!response.data.data) {
      throw new Error('Unable to create your account. Please try again.');
    }

    const { user, accessToken } = response.data.data;

    if (!user) {
      throw new Error('Unable to create your account. Please try again.');
    }

    this.setAuthState(user, accessToken);

    return response.data.data;
  }

  async verifyEmail(email: string, code: string): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this._client.post(
      '/api/auth/verify',
      { email, code }
    );

    if (!response.data.success) {
      const error = response.data as any;
      const errorMessage = error.error || error.message || 'Verification failed';
      throw new Error(errorMessage);
    }

    if (!response.data.data) {
      throw new Error('Unable to verify your account. Please try again.');
    }

    const { user, accessToken } = response.data.data as any;
    if (!user) {
      throw new Error('Unable to verify your account. Please try again.');
    }

    this.setAuthState(user, accessToken);

    return response.data.data;
  }

  async getSession(): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<ApiResponse<LoginResponse>> = await this._client.get(
        '/api/auth/session',
        { timeout: 12000 }
      );

      if (!response.data.success || !response.data.data?.user) {
        this.clearAuthState();
        throw new Error((response.data as any).error || 'No active session');
      }

      this.setAuthState(response.data.data.user, response.data.data.accessToken);
      return response.data.data;
    } catch (err: any) {
      if (err?.response?.status === 401) {
        this.clearAuthState();
      }
      throw err;
    }
  }

  async refreshToken(): Promise<void> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this._client.post(
      '/api/auth/refresh'
    );
    if (!response.data.success || !response.data.data?.user) {
      this.clearAuthState();
      throw new Error((response.data as any).error || 'Session refresh failed');
    }
    this.setAuthState(response.data.data.user, response.data.data.accessToken);
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/api/auth/logout', {}, {
        withCredentials: true
      });
    } catch {
      // Silently ignore — user is already logged out locally
    } finally {
      this.clearAuthState();
    }
  }

  // Business methods
  async getBusinessDetails(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/businesses/me'
    );
    return response.data.data;
  }

  async getDashboardMetrics(params?: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/reports/dashboard',
      { params }
    );
    return response.data.data;
  }

  async updateBusinessDetails(businessData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      '/api/businesses/me',
      businessData
    );
    return response.data.data;
  }

  // Sales methods
  async getSales(filters?: any): Promise<PaginatedResponse<any> & { counts?: any }> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<any> & { counts?: any }>> =
      await this.client.get('/api/sales', { params: filters });
    return response.data.data!;
  }

  async getSale(saleId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/sales/${saleId}`
    );
    return response.data.data!;
  }

  async createSale(saleData: any, locationId?: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/sales',
      saleData,
      locationId
        ? {
          headers: {
            'X-Location-Id': locationId,
          },
        }
        : undefined
    );
    return response.data.data!;
  }

  async exportCustomersCsv(): Promise<Blob> {
    const response = await this._client.get('/api/customers/export/csv', {
      responseType: 'blob',
    });
    return response.data;
  }

  async getCustomers(filters?: any): Promise<PaginatedResponse<any>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<any>>> =
      await this.client.get('/api/customers', { params: filters });
    return response.data.data!;
  }

  async getCustomer(customerId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/customers/${customerId}`
    );
    return response.data.data;
  }

  async createCustomer(customerData: {
    firstName: string;
    lastName?: string;
    email: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      postcode?: string;
      country?: string;
    };
    notes?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/customers',
      customerData
    );
    return response.data.data!;
  }

  async deleteCustomer(customerId: string): Promise<void> {
    await this._client.delete(`/api/customers/${customerId}`);
  }

  async updateCustomer(customerId: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/customers/${customerId}`,
      data,
    );
    return response.data.data;
  }

  async exportCustomerGdprData(customerId: string): Promise<{ blob: Blob, filename: string }> {
    const response = await this._client.get(`/api/customers/${customerId}/export`, {
      responseType: 'blob',
    });
    
    // Extract filename from Content-Disposition header if possible
    let filename = `data-export-${customerId}.json`;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition && contentDisposition.includes('filename=')) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }
    
    return { blob: response.data, filename };
  }

  async anonymizeCustomerGdprData(customerId: string): Promise<void> {
    await this._client.post(`/api/customers/${customerId}/anonymize`);
  }

  // Product methods
  async getProducts(filters?: any, locationId?: string): Promise<PaginatedResponse<any> & { counts?: any }> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<any> & { counts?: any }>> =
      await this.client.get('/api/products', {
        params: filters,
        ...(locationId
          ? {
            headers: {
              'X-Location-Id': locationId,
            },
          }
          : {}),
      });
    return response.data.data!;
  }

  async createProduct(productData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/products',
      productData
    );
    return response.data.data!;
  }

  async searchProducts(query: string, locationId?: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.client.get(
      '/api/products/search',
      {
        params: { q: query },
        ...(locationId
          ? {
            headers: {
              'X-Location-Id': locationId,
            },
          }
          : {}),
      }
    );
    return response.data.data || [];
  }

  async lookupBarcode(barcode: string): Promise<{
    found: boolean;
    source?: 'cache' | 'openfoodfacts' | 'upcitemdb';
    barcode: string;
    name?: string;
    brand?: string;
    description?: string;
    category?: string;
    images?: string[];
    unitType?: string;
    quantity?: string;
    ingredients?: string;
    message?: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.client.get(
      '/api/products/barcode-lookup',
      {
        params: { barcode: barcode.trim() },
      }
    );
    return response.data.data || { found: false, barcode };
  }

  async getProductCategories(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.client.get(
      '/api/products/categories'
    );
    return response.data.data || [];
  }

  // Category CRUD (business-owned categories)
  async getCategories(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.client.get(
      '/api/categories'
    );
    return response.data.data || [];
  }

  async getCategory(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.client.get(
      `/api/categories/${id}`
    );
    return response.data.data!;
  }

  async createCategory(data: { name: string; sortOrder?: number; emoji?: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.client.post(
      '/api/categories',
      data
    );
    return response.data.data!;
  }

  async updateCategory(
    id: string,
    data: { name?: string; sortOrder?: number; emoji?: string },
  ): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.client.put(
      `/api/categories/${id}`,
      data
    );
    return response.data.data!;
  }

    async deleteCategory(id: string): Promise<void> {
    await this.client.delete(`/api/categories/${id}`);
  }

  async addProductsToCategory(categoryId: string, productIds: string[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.client.post(
      `/api/categories/${categoryId}/products`,
      { productIds }
    );
    return response.data.data;
  }

  async removeProductsFromCategory(categoryId: string, productIds: string[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.client.delete(
      `/api/categories/${categoryId}/products`,
      { data: { productIds } }
    );
    return response.data.data;
  }

  async getProduct(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/products/${id}`
    );
    return response.data.data!;
  }

  async getProductActivity(id: string): Promise<any[]> { 
    const response = await this._client.get(`/api/products/${id}/activity`); 
    return response.data.data || []; 
  }

  async getAllProductActivity(): Promise<any[]> { 
    const response = await this._client.get('/api/products/activity/all'); 
    return response.data.data || []; 
  }

  async updateProduct(id: string, productData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/products/${id}`,
      productData
    );
    return response.data.data!;
  }

  async deleteProduct(id: string, options?: { permanent?: boolean }): Promise<void> {
    const query = options?.permanent ? '?permanent=true' : '';
    await this._client.delete(`/api/products/${id}${query}`);
  }

  async getLowStockItems(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<{ items: any[] }>> =
      await this._client.get('/api/inventory/low-stock');
    const data = response.data.data;
    return data?.items ?? [];
  }

  // Global Products methods
  async getGlobalProducts(filters?: any): Promise<PaginatedResponse<any>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<any>>> =
      await this.client.get('/api/products/global', { params: filters });
    return response.data.data!;
  }

  async getGlobalProductCategories(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> =
      await this.client.get('/api/products/global/categories');
    return response.data.data || [];
  }

  // Suggested Products methods
  async getSuggestedProducts(filters?: any): Promise<PaginatedResponse<any>> {
    const response: AxiosResponse<ApiResponse<PaginatedResponse<any>>> =
      await this.client.get('/api/products/suggested', { params: filters });
    return response.data.data!;
  }

  async importGlobalProduct(globalProductId: string, customFields?: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      `/api/products/suggested/import/${globalProductId}`,
      customFields || {}
    );
    return response.data.data!;
  }

  async importMultipleGlobalProducts(products: Array<{ globalProductId: string; customFields?: any }>): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/products/suggested/import-multiple',
      { products }
    );
    return response.data.data!;
  }

  // Upload methods
  async uploadImage(file: File, folder?: string): Promise<any> {
    const formData = new FormData();
    formData.append('image', file);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      `/api/upload/image${folder ? `?folder=${folder}` : ''}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  async uploadImages(files: File[], folder?: string): Promise<any[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));

    const response: AxiosResponse<ApiResponse<any[]>> = await this.client.post(
      `/api/upload/images${folder ? `?folder=${folder}` : ''}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data || [];
  }

  async deleteImage(publicId: string): Promise<void> {
    await this._client.delete(`/api/upload/image/${publicId}`);
  }

  // Data import methods
  async previewDataImport(
    entityType: string,
    file: File,
    columnMapping?: Record<string, string>
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (columnMapping) {
      formData.append('columnMapping', JSON.stringify(columnMapping));
    }

    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      `/api/data-import/${entityType.toLowerCase()}/preview`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  async executeDataImport(
    entityType: string,
    file: File,
    options?: {
      duplicateStrategy?: 'UPDATE' | 'SKIP' | 'ERROR';
      locationId?: string;
      columnMapping?: Record<string, string>;
    }
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.duplicateStrategy) {
      formData.append('duplicateStrategy', options.duplicateStrategy);
    }
    if (options?.locationId) {
      formData.append('locationId', options.locationId);
    }
    if (options?.columnMapping) {
      formData.append('columnMapping', JSON.stringify(options.columnMapping));
    }

    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      `/api/data-import/${entityType.toLowerCase()}/execute`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  async downloadImportTemplate(
    entityType: string,
    format: 'csv' | 'xlsx' = 'csv'
  ): Promise<Blob> {
    const response = await this._client.get(
      `/api/data-import/${entityType.toLowerCase()}/template`,
      {
        params: { format },
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async importCSV(file: File, duplicateStrategy: 'UPDATE' | 'SKIP' | 'ERROR' = 'UPDATE'): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('duplicateStrategy', duplicateStrategy);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/data-import/csv',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  async importExcel(file: File, duplicateStrategy: 'UPDATE' | 'SKIP' | 'ERROR' = 'UPDATE'): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('duplicateStrategy', duplicateStrategy);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/data-import/excel',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  }

  // Sales Reports methods
  async getDailySalesReport(date?: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/sales/reports/daily',
      { params: { date } }
    );
    return response.data.data;
  }

  // Export methods
  async exportSalesCSV(filters?: any): Promise<Blob> {
    const response = await this._client.get('/api/sales/export/csv', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.getCurrentUser();
  }

  getCurrentUser(): any {
    try {
      const userStr = localStorage.getItem(USER_STORAGE_KEY);
      if (!userStr || userStr === 'undefined' || userStr === 'null') return null;
      return JSON.parse(userStr);
    } catch {
      localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
  }

  // Inventory methods
  async getInventorySummary(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/inventory/summary'
    );
    return response.data.data;
  }

  async getInventoryLowStock(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/inventory/low-stock'
    );
    return response.data.data;
  }

  async getInventoryOutOfStock(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/inventory/out-of-stock'
    );
    return response.data.data;
  }

  async getInventoryStock(filters?: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/inventory/stock',
      { params: filters }
    );
    return response.data.data;
  }

  async createStockAdjustment(data: { productId: string; adjustmentType: string; quantityChange: number; reason?: string }): Promise<{ id?: string }> {
    const response: AxiosResponse<ApiResponse<{ id?: string }>> = await this._client.post(
      '/api/inventory/adjustment',
      { ...data, reason: data.reason ?? 'Offline sync' }
    );
    return response.data.data ?? {};
  }

  async getStockMovements(filters?: { page?: number; limit?: number; productId?: string; adjustmentType?: string; search?: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      '/api/inventory/movements',
      { params: filters }
    );
    return response.data.data;
  }

  getAuthHeader(): string {
    return '';
  }

  // ============================================
  // PHASE 2: HR & Staff Management Methods
  // ============================================

  // Staff Management
  async getStaff(filters?: any): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.employmentType) params.append('employmentType', filters.employmentType);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.search) params.append('search', filters.search);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/hr/staff?${params.toString()}`
    );
    // Ensure we always return an array
    const data = response.data.data;
    return Array.isArray(data) ? data : [];
  }

  async getStaffById(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(`/api/hr/staff/${id}`);
    return response.data.data;
  }

  async updateStaffEmployment(id: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/staff/${id}/employment`,
      data
    );
    return response.data.data;
  }

  async setUserPassword(userId: string, newPassword: string): Promise<void> {
    await this._client.post('/api/users/set-password', { userId, newPassword });
  }

  async updateStaffWage(id: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/staff/${id}/wage`,
      data
    );
    return response.data.data;
  }

  async createStaff(data: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role: string;
    employmentType?: string;
    employeeNumber?: string;
    startDate?: string;
    locationId?: string;
    assignedStore?: string;
    hourlyRate?: number;
    overtimeRate?: number;
    taxWithholdingRate?: number;
    dateOfBirth?: string;
    address?: any;
    emergencyContact?: any;
    password: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post(
      '/api/hr/staff',
      data
    );
    return response.data.data;
  }

  async updateStaffBanking(id: string, bankAccountData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/staff/${id}/banking`,
      { bankAccountData }
    );
    return response.data.data;
  }

  async deactivateStaff(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/staff/${id}/deactivate`
    );
    return response.data.data;
  }

  async reactivateStaff(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/staff/${id}/reactivate`
    );
    return response.data.data;
  }

  async deleteStaff(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.delete(
      `/api/hr/staff/${id}`
    );
    return response.data.data;
  }

  // Time Tracking
  async getTimeEntries(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/hr/time/entries?${params.toString()}`
    );
    return response.data.data;
  }

  async getDailyTimesheet(date: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/hr/time/reports/daily?date=${date}`
    );
    return response.data.data;
  }

  async getWeeklyTimesheet(userId: string, weekStart: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/hr/time/reports/weekly?userId=${userId}&weekStart=${weekStart}`
    );
    return response.data.data;
  }

  async approveTimeEntry(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/time/entries/${id}/approve`
    );
    return response.data.data;
  }

  async rejectTimeEntry(id: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/hr/time/entries/${id}/reject`
    );
    return response.data.data;
  }

  // Shifts
  async getShifts(filters?: any): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.location) params.append('location', filters.location);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/shifts?${params.toString()}`
    );
    const payload = response.data.data;
    // Backend returns { data: { data: Shift[] } }; unwrap to the array
    if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
      return payload.data;
    }
    return payload;
  }

  async getMyShifts(startDate?: string, endDate?: string): Promise<any> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/shifts/my-shifts?${params.toString()}`
    );
    const payload = response.data.data;
    // Backend returns { data: { data: Shift[] } }; unwrap to the array
    if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
      return payload.data;
    }
    return payload;
  }

  async createShift(data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.post('/api/shifts', data);
    return response.data.data;
  }

  async updateShift(id: string, data: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/shifts/${id}`,
      data
    );
    return response.data.data;
  }

  async assignShift(id: string, userId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this._client.put(
      `/api/shifts/${id}/assign`,
      { userId }
    );
    return response.data.data;
  }

  async deleteShift(id: string): Promise<void> {
    await this._client.delete(`/api/shifts/${id}`);
  }

  async getInAppNotifications(params?: { page?: number; limit?: number; unreadOnly?: boolean }) {
    const search = new URLSearchParams();
    if (params?.page != null) search.set('page', String(params.page));
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.unreadOnly) search.set('unreadOnly', 'true');
    const q = search.toString();
    const response: AxiosResponse<ApiResponse<any>> = await this._client.get(
      `/api/notifications${q ? `?${q}` : ''}`
    );
    return response.data.data;
  }

  async markInAppNotificationRead(id: string): Promise<void> {
    await this._client.patch(`/api/notifications/${id}/read`);
  }

  async markAllInAppNotificationsRead(): Promise<{ marked: number }> {
    const response: AxiosResponse<ApiResponse<{ marked: number }>> = await this._client.post(
      '/api/notifications/read-all'
    );
    return response.data.data!;
  }
}

export const apiService = new ApiService();
export default apiService;

